import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

export default function ShowReel() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      data-testid="show-reel-section"
      className="relative bg-ink-900 py-20 md:py-28 border-b border-ink-600"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="tag-kicker">The show reel</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.15] tracking-normal mt-3 max-w-3xl">
              What <span className="italic text-amber">$250K+ in sync</span>{" "}
              actually looks like.
            </h2>
          </div>
          <p className="text-white/60 text-sm max-w-sm">
            Independent artists soundtracking premium TV, film and brand spots.
            Placed at premium rates.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="show-reel-player"
          className="relative group overflow-hidden border border-ink-600 shadow-[0_20px_80px_-20px_rgba(34,197,94,0.15)] bg-black"
        >
          <video
            ref={videoRef}
            data-testid="show-reel-video"
            src="/um-show-reel.mp4"
            poster="/um-show-reel-poster.jpg"
            className="w-full h-auto block aspect-video object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />

          {/* Controls overlay */}
          <div className="absolute inset-0 flex items-end justify-between p-4 md:p-6 pointer-events-none">
            <div className="flex gap-2 pointer-events-auto">
              <button
                type="button"
                data-testid="show-reel-play-toggle"
                onClick={togglePlay}
                aria-label={playing ? "Pause" : "Play"}
                className="h-11 w-11 flex items-center justify-center bg-black/70 hover:bg-amber hover:text-black text-white backdrop-blur-md border border-white/20 rounded-sm transition-colors"
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button
                type="button"
                data-testid="show-reel-mute-toggle"
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
                className="h-11 flex items-center gap-2 px-4 bg-black/70 hover:bg-amber hover:text-black text-white backdrop-blur-md border border-white/20 rounded-sm transition-colors text-xs font-mono uppercase tracking-widest"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                {muted ? "Tap for sound" : "Sound on"}
              </button>
            </div>

            <div className="hidden md:block pointer-events-auto">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70 bg-black/50 backdrop-blur-md border border-white/10 px-3 py-2 rounded-sm">
                Underground Mix · Show Reel
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
